import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../shared/helpers/icons';
import { IUserInfo } from '../../shared/models/contact';
import { IEopDocument } from '../../shared/models/eopDocument';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../shared/services/userData.service';
import { ESectionTypes } from '../../shared/models/eop_types.enum';
import { EopPlanSlug, buildPlanPageUrl, getBuildingIdFromUrl, getPlanSlugFromUrl } from '../eop-config/eop-page-config';

interface ITrainingLink {
  label: string;
  url: string;
}

interface ITrainingDoc {
  name: string;
  title: string;
  recommendedCompletion: string;
  mdePolicy?: ITrainingLink;
  msStateLaw?: ITrainingLink;
  webLinks: ITrainingLink[];
}

@Component({
  selector: 'app-sso-training-resources',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './sso-training-resources.component.html',
  styleUrl: './sso-training-resources.component.css'
})
export class SsoTrainingResourcesComponent {
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentPageRoute: string | null = null;
  currentPlan: EopPlanSlug = 'al';
  currentBuildingIdFromRoute: string | null = null;
  eopDoc: IEopDocument | null = null;
  eopSectionTypeId = ESectionTypes["Mandatory Training Resources"];
  eopSubSectionTypeId: number | null = null;
  
  trainingDocs: ITrainingDoc[] = [
    {
      name: 'MDE / NASRO Course',
      title: 'Completion of the MDE School Safety Officer Basic Course or NASRO School Safety Officer Basic Course',
      recommendedCompletion: 'each year',
      webLinks: [
        { label: 'MDE School Safety', url: 'https://www.mdek12.org/OSS/SS' },
        { label: 'National Association of School Resource Officers (NASRO)', url: 'https://www.nasro.org/training/training-courses/' }
      ]
    },
    {
      name: 'MDE Sanctioned In-service',
      title: 'Forty (40) hours of MDE sanctioned in-service',
      recommendedCompletion: 'each year',
      webLinks: [
        { label: 'MDE School Safety', url: 'https://www.mdek12.org/OSS/SS' }
      ]
    }
  ];
  selectedDoc: ITrainingDoc = this.trainingDocs[0];
  
  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDataService: UserDataService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.currentPlan = getPlanSlugFromUrl(this.router.url);
    this.currentBuildingIdFromRoute = getBuildingIdFromUrl(this.router.url);
    this.route.paramMap.subscribe(params => {
      this.currentPageRoute = params.get('pageRoute');
    });
  }

  getCurrentRoute(): string | null {
    const url = this.router.url.replace(/\/+$/, ''); // remove trailing slash
    const segments = url.split('/');
    return segments[-1] || segments[segments.length - 1]; // get last segment
  }

  selectDoc(doc: ITrainingDoc) {
    this.selectedDoc = doc;
  }
  
  navigateToPage(route: string): void {
    const buildingId = this.currentBuildingIdFromRoute ?? this.currentBuildingId;
    if (!buildingId) {
      return;
    }

    this.router.navigateByUrl(buildPlanPageUrl(this.currentPlan, buildingId, route));
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

}
